/**
 * Root Application Component
 *
 * App shell with JWT authentication routing and lazy-loaded pages.
 */

import { AuthProvider, useAuth } from '@/features/auth';
import { AuthSplitLayout } from '@/features/auth/components';
import { ErrorBoundary } from '@/shared/components/error-boundary';
import { ErrorCard } from '@/shared/components/error-card';
import { AppLayout } from '@/shared/components/layout';
import { CardSkeleton } from '@/shared/components/loading-skeleton';
import { QueryErrorBoundary } from '@/shared/components/query-error-boundary';
import { Toaster } from '@/shared/components/ui/toaster';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useRouteError } from 'react-router-dom';

// Lazy-loaded page components for code splitting
const DashboardPage = lazy(() => import('@/features/dashboard/dashboard-page').then((m) => ({ default: m.DashboardPage })));
const TransactionsPage = lazy(() => import('@/features/transactions/transactions-page').then((m) => ({ default: m.TransactionsPage })));
const SettleUpPage = lazy(() => import('@/features/settle-up/settle-up-page').then((m) => ({ default: m.SettleUpPage })));
const BudgetsPage = lazy(() => import('@/features/budgets/budgets-page').then((m) => ({ default: m.BudgetsPage })));
const AccountsPage = lazy(() => import('@/features/accounts').then((m) => ({ default: m.AccountsPage })));
const CategoriesPage = lazy(() => import('@/features/categories').then((m) => ({ default: m.CategoriesPage })));
const GoalsPage = lazy(() => import('@/features/goals/components/goals-page').then((m) => ({ default: m.GoalsPage })));
const SubscriptionsPage = lazy(() => import('@/features/subscriptions/subscriptions-page').then((m) => ({ default: m.SubscriptionsPage })));
const AuditLogPage = lazy(() => import('@/features/audit-log/audit-log-page').then((m) => ({ default: m.AuditLogPage })));
const ReportsPage = lazy(() => import('@/features/reports/reports-page').then((m) => ({ default: m.ReportsPage })));
const ToolsPage = lazy(() => import('@/features/tools/tools-page').then((m) => ({ default: m.ToolsPage })));
const NotificationsPage = lazy(() =>
  import('@/features/notifications/components/notifications-page').then((m) => ({ default: m.NotificationsPage }))
);

// OAuth callback page (outside AppLayout — transitional page)
const PlaidOAuthCallback = lazy(() =>
  import('@/features/accounts/pages/plaid-oauth-callback').then((m) => ({ default: m.PlaidOAuthCallback }))
);

// Landing & policy pages
const LandingPage = lazy(() => import('@/features/landing/landing-page').then((m) => ({ default: m.LandingPage })));
const PrivacyPolicyPage = lazy(() => import('@/features/landing/privacy-policy-page').then((m) => ({ default: m.PrivacyPolicyPage })));
const DataRetentionPage = lazy(() => import('@/features/landing/data-retention-page').then((m) => ({ default: m.DataRetentionPage })));

// Auth pages
const SignupPage = lazy(() => import('@/features/auth/signup-page').then((m) => ({ default: m.SignupPage })));
const LoginPage = lazy(() => import('@/features/auth/login-page').then((m) => ({ default: m.LoginPage })));
const VerifyEmail = lazy(() => import('@/features/auth/components/verify-email').then((m) => ({ default: m.VerifyEmail })));
const InvitedSignupForm = lazy(() =>
  import('@/features/auth/components/invited-signup-form').then((m) => ({ default: m.InvitedSignupForm }))
);
const ForgotPasswordForm = lazy(() =>
  import('@/features/auth/components/forgot-password-form').then((m) => ({ default: m.ForgotPasswordForm }))
);
const ResetPasswordForm = lazy(() =>
  import('@/features/auth/components/reset-password-form').then((m) => ({ default: m.ResetPasswordForm }))
);

/**
 * Loading fallback shown while page chunks are loading
 */
function LoadingFallback() {
  return (
    <div className='p-6 space-y-4'>
      <CardSkeleton />
      <CardSkeleton />
    </div>
  );
}

/**
 * Auth page layout wrapper
 */
function AuthPageLayout({ children }: { children: React.ReactNode }) {
  return <div className='flex min-h-screen items-center justify-center bg-background p-4'>{children}</div>;
}

/**
 * Protected route component that redirects to login if not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  return <>{children}</>;
}

/**
 * Auth-aware route wrapper for public pages (redirects if already logged in)
 */
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to='/dashboard' replace />;
  }

  return <>{children}</>;
}

/**
 * Landing page route — redirects to dashboard if already authenticated
 */
function LandingRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (isAuthenticated) {
    return <Navigate to='/dashboard' replace />;
  }

  return <>{children}</>;
}

/**
 * Route-level error element for React Router's data router.
 * Automatically resets on navigation (no stale error across routes).
 */
function RouteError() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
  return (
    <div className='p-6'>
      <ErrorCard title='Something went wrong' message={message} onRetry={() => window.location.reload()} />
    </div>
  );
}

/**
 * Root layout that provides Suspense boundary
 */
function RootLayout() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Router configuration using Data Router API for view transitions support
 */
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public landing & policy routes
      {
        path: '/',
        element: (
          <LandingRoute>
            <LandingPage />
          </LandingRoute>
        )
      },
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/data-retention', element: <DataRetentionPage /> },
      // Public auth routes
      {
        path: '/login',
        element: (
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        )
      },
      {
        path: '/signup',
        element: (
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        )
      },
      {
        path: '/signup/invite/:token',
        element: (
          <AuthPageLayout>
            <InvitedSignupForm />
          </AuthPageLayout>
        )
      },
      {
        path: '/verify-email/:token',
        element: (
          <AuthPageLayout>
            <VerifyEmail />
          </AuthPageLayout>
        )
      },
      {
        path: '/forgot-password',
        element: (
          <AuthSplitLayout>
            <ForgotPasswordForm />
          </AuthSplitLayout>
        )
      },
      {
        path: '/reset-password/:token',
        element: (
          <AuthPageLayout>
            <ResetPasswordForm />
          </AuthPageLayout>
        )
      },
      // OAuth callback — protected but outside AppLayout (transitional page)
      {
        path: '/oauth-callback',
        element: (
          <ProtectedRoute>
            <PlaidOAuthCallback />
          </ProtectedRoute>
        )
      },
      // Protected app routes
      {
        element: (
          <ProtectedRoute>
            <QueryErrorBoundary>
              <AppLayout />
            </QueryErrorBoundary>
          </ProtectedRoute>
        ),
        errorElement: <RouteError />,
        children: [
          { path: '/dashboard', element: <DashboardPage /> },
          { path: '/transactions', element: <TransactionsPage /> },
          { path: '/settle-up', element: <SettleUpPage /> },
          { path: '/budgets', element: <BudgetsPage /> },
          { path: '/reports', element: <ReportsPage /> },
          { path: '/goals', element: <GoalsPage /> },
          { path: '/subscriptions', element: <SubscriptionsPage /> },
          { path: '/categories', element: <CategoriesPage /> },
          { path: '/accounts', element: <AccountsPage /> },
          { path: '/tools', element: <ToolsPage /> },
          { path: '/audit-log', element: <AuditLogPage /> },
          { path: '/notifications', element: <NotificationsPage /> }
        ]
      },
      // Catch-all redirect
      { path: '*', element: <Navigate to='/dashboard' replace /> }
    ]
  }
]);

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <RouterProvider router={router} />
        <Toaster />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
